import { Alert, Badge, Button, Field, Input, Select } from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  FileUp,
  Upload,
  UserCheck,
  XCircle,
} from 'lucide-react'
import { useMemo, useState } from 'react'

import { errorMessage } from '@/api/errors'
import { useImportLeads, useMembers } from '@/api/queries'
import type { LeadImportResult, LeadImportRow, LeadSource } from '@/api/types'
import { LEAD_SOURCES } from '@/api/types'
import { PageHeader } from '@/components/layout/app-shell'
import { readCsvFile, type ParsedCsv } from '@/lib/csv'

/**
 * Import di una lista di contatti — CRM-02.
 *
 * ## Gli stessi tre passi dell'import degli iscritti, e per le stesse ragioni
 *
 * **Il file non arriva al server**: lo legge il browser e manda righe già interpretate. È ciò
 * che rende possibile far *mappare* le colonne a chi sta guardando il file — «Cellulare» e
 * «Tel.» sono lo stesso campo e lo sa l'operatore — e significa che di quella lista non resta
 * copia in un file temporaneo o in un log.
 *
 * Il lettore CSV è **quello di ANA-07**, in `lib/csv.ts`: due parser copiati divergono, e
 * divergono spostando le colonne, che è il modo in cui un import sembra riuscito e non lo è.
 *
 * ## L'anteprima risponde a una domanda diversa da quella degli iscritti
 *
 * Là la domanda era «queste dodici esistono già, sono queste persone?». Qui è **«quante di
 * queste sono già mie clienti?»**, e la risposta conta perché le automazioni di CRM-05 scrivono
 * ai contatti: un socio rientrato nel funnel riceve l'invito a provare gratis la palestra di cui
 * paga l'abbonamento. Il server li tiene fuori da sé — la pagina lo **dice**, con il numero e
 * con il collegamento alla scheda di ognuno, perché la domanda che segue è sempre «chi è?».
 *
 * ## Due righe sono la stessa persona per email o telefono, mai per nome
 *
 * In una lista di una fiera ci sono due Rossi Marco. Il numero si confronta per sole cifre —
 * `+39 333 123 4567` e `3331234567` sono lo stesso — e si conserva come è scritto.
 *
 * ⚠️ Una riga **senza recapito** non entra: un contatto che non si può richiamare è un appunto,
 * e la pagina lo dice prima dell'invio invece di lasciarlo scoprire nel report.
 */
export const Route = createFileRoute('/_app/contatti/import')({
  component: LeadImportPage,
})

/** I campi che l'import accetta. Niente stato e niente punteggio: vedi il contract. */
const FIELDS = [
  { key: 'firstName', label: 'Nome', required: true },
  { key: 'lastName', label: 'Cognome', required: true },
  { key: 'email', label: 'Email', required: false },
  { key: 'phone', label: 'Telefono', required: false },
  { key: 'note', label: 'Note', required: false },
] as const

type FieldKey = (typeof FIELDS)[number]['key']
type ColumnChoice = FieldKey | ''

const COLUMN_OPTIONS: { value: ColumnChoice; label: string }[] = [
  { value: '', label: 'Ignora' },
  ...FIELDS.map((field) => ({
    value: field.key satisfies ColumnChoice,
    label: field.required ? `${field.label} *` : field.label,
  })),
]

const SOURCE_LABELS: Record<LeadSource, string> = {
  walk_in: 'Passato in palestra',
  web_form: 'Modulo web',
  referral: 'Presentato da un iscritto',
  social: 'Social',
  campaign: 'Campagna',
  phone: 'Telefono',
  other: 'Altro',
}

const DUPLICATE_OPTIONS: { value: 'skip' | 'update'; label: string }[] = [
  { value: 'skip', label: 'Lascia stare' },
  { value: 'update', label: 'Aggiorna i campi presenti nel file' },
]

/**
 * Accoppiamento automatico fra intestazione e campo — **propone**, non decide.
 *
 * Le forme sono quelle delle liste vere: un export di rubrica del telefono, un foglio di una
 * fiera, un vecchio gestionale. Indovinare bene nove volte su dieci e sbagliare in silenzio la
 * decima è peggio che chiedere, quindi ogni accoppiamento resta modificabile.
 */
const ALIASES: Record<string, FieldKey> = {
  nome: 'firstName',
  'nome di battesimo': 'firstName',
  cognome: 'lastName',
  email: 'email',
  'e-mail': 'email',
  mail: 'email',
  posta: 'email',
  'indirizzo email': 'email',
  telefono: 'phone',
  cellulare: 'phone',
  cell: 'phone',
  tel: 'phone',
  'tel.': 'phone',
  numero: 'phone',
  note: 'note',
  annotazioni: 'note',
  commento: 'note',
}

function LeadImportPage() {
  const [parsed, setParsed] = useState<ParsedCsv | null>(null)
  const [mapping, setMapping] = useState<Record<number, ColumnChoice>>({})
  const [source, setSource] = useState<LeadSource>('other')
  const [onDuplicate, setOnDuplicate] = useState<'skip' | 'update'>('skip')
  const [assignedMemberId, setAssignedMemberId] = useState<string>('')
  const [nextActionAt, setNextActionAt] = useState('')
  const [preview, setPreview] = useState<LeadImportResult | null>(null)
  const [applied, setApplied] = useState<LeadImportResult | null>(null)
  const [readError, setReadError] = useState<string | null>(null)

  const importer = useImportLeads()
  const operators = useMembers({ status: 'active', limit: 100 })

  async function onFile(file: File): Promise<void> {
    setPreview(null)
    setApplied(null)
    setReadError(null)
    const result = await readCsvFile(file)
    if ('error' in result) {
      setReadError(result.error)
      return
    }
    setParsed(result)
    const guessed: Record<number, ColumnChoice> = {}
    result.headers.forEach((header, index) => {
      guessed[index] = ALIASES[header.toLowerCase()] ?? ''
    })
    setMapping(guessed)
  }

  const mapped = useMemo((): LeadImportRow[] => {
    if (parsed === null) return []
    const columns = Object.entries(mapping).filter(([, field]) => field !== '')

    return parsed.rows.map((cells, rowIndex) => {
      // `line` è la riga **nel file**: +2 perché l'intestazione è la prima e si conta da uno.
      const row: Record<string, unknown> = { line: rowIndex + 2 }
      for (const [rawIndex, field] of columns) {
        const value = cells[Number(rawIndex)] ?? ''
        if (value.trim() === '') continue
        row[field] = value.trim()
      }
      return row as unknown as LeadImportRow
    })
  }, [parsed, mapping])

  const missingRequired = FIELDS.filter(
    (field) => field.required && !Object.values(mapping).includes(field.key),
  )
  const hasContact = Object.values(mapping).some((field) => field === 'email' || field === 'phone')
  // Quante righe non hanno né email né telefono: il server le rifiuta una per una, e saperlo
  // prima evita di scoprire nel report che mezza lista era una rubrica senza numeri.
  const withoutContact = mapped.filter(
    (row) => (row.email ?? '') === '' && (row.phone ?? '') === '',
  ).length
  const tooLarge = mapped.length > 500

  async function run(dryRun: boolean): Promise<void> {
    const result = await importer.mutateAsync({
      leads: mapped,
      source,
      dryRun,
      onDuplicate,
      ...(assignedMemberId === '' ? {} : { assignedMemberId }),
      ...(nextActionAt === '' ? {} : { nextActionAt }),
    })
    if (dryRun) setPreview(result)
    else setApplied(result)
  }

  const operatorOptions = [
    { value: '', label: 'Nessuno' },
    ...(operators.data?.data ?? []).map((member) => ({
      value: member.memberId,
      label: member.name ?? member.email,
    })),
  ]

  return (
    <div className="space-y-4">
      <PageHeader
        title="Importa contatti"
        description="Da un file CSV: una rubrica, un foglio di una fiera, l’export di un altro gestionale."
        actions={
          <Button variant="ghost" asChild>
            <Link to="/contatti">
              <ArrowLeft className="size-4" />
              Torna ai contatti
            </Link>
          </Button>
        }
      />

      {/* Passo 1 — il file */}
      <section className="bg-card rounded-lg border p-4">
        <h2 className="font-semibold">1. Il file</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Il file <strong>non viene caricato</strong>: lo legge questo browser e al server arrivano
          solo le righe, già accoppiate ai campi. Una lista di contatti è un blocco di dati
          personali, e non caricarla significa non lasciarne copia da nessuna parte.
        </p>
        <label className="mt-3 flex w-fit cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm">
          <FileUp className="size-4" />
          <span>{parsed?.filename ?? 'Scegli un CSV…'}</span>
          <input
            type="file"
            accept=".csv,text/csv,text/plain"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file) void onFile(file)
            }}
          />
        </label>
        {readError !== null ? (
          <Alert variant="danger" className="mt-3">
            {readError}
          </Alert>
        ) : null}
        {parsed !== null ? (
          <p className="text-muted-foreground mt-2 text-xs">
            {mapped.length} righe, separatore{' '}
            <code>{parsed.separator === '\t' ? 'tabulazione' : parsed.separator}</code>.
          </p>
        ) : null}
      </section>

      {/* Passo 2 — le colonne e ciò che vale per tutta la lista */}
      {parsed !== null ? (
        <section className="bg-card rounded-lg border p-4">
          <h2 className="font-semibold">2. Le colonne</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            L’accoppiamento è <strong>proposto</strong>, non deciso: controllalo. Le colonne
            lasciate su «Ignora» non vengono importate.
          </p>

          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {parsed.headers.map((header, index) => (
              <Field
                key={`${header}-${index}`}
                label={header === '' ? `Colonna ${index + 1}` : header}
                hint={`Es.: ${parsed.rows[0]?.[index] ?? '—'}`}
              >
                {(props) => (
                  <Select
                    {...props}
                    value={mapping[index] ?? ''}
                    onValueChange={(value) =>
                      setMapping((current) => ({ ...current, [index]: value as ColumnChoice }))
                    }
                    items={COLUMN_OPTIONS}
                  />
                )}
              </Field>
            ))}
          </div>

          {missingRequired.length > 0 ? (
            <Alert variant="danger" className="mt-3">
              Manca l’accoppiamento di: {missingRequired.map((f) => f.label).join(', ')}.
            </Alert>
          ) : null}

          {!hasContact ? (
            <Alert variant="danger" className="mt-3">
              <AlertTriangle className="size-4" />
              <span>
                Nessuna colonna è accoppiata a <strong>email</strong> o <strong>telefono</strong>:
                senza un recapito nessuna riga entra. Un contatto che non si può richiamare è un
                appunto, non un contatto.
              </span>
            </Alert>
          ) : withoutContact > 0 ? (
            <Alert variant="warning" className="mt-3">
              <AlertTriangle className="size-4" />
              <span>
                {withoutContact} righe su {mapped.length} non hanno né email né telefono: quelle
                verranno segnalate e non importate.
              </span>
            </Alert>
          ) : null}

          <h3 className="mt-4 font-medium">Cosa vale per tutta la lista</h3>
          <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field
              label="Da dove arriva"
              hint="Una lista ha un’origine sola. «Altro» è una risposta."
            >
              {(props) => (
                <Select
                  {...props}
                  value={source}
                  onValueChange={(value) => setSource(value as typeof source)}
                  items={LEAD_SOURCES.map((value) => ({ value, label: SOURCE_LABELS[value] }))}
                />
              )}
            </Field>
            <Field label="Chi la segue" hint="Assegna tutti i contatti nuovi a un operatore.">
              {(props) => (
                <Select
                  {...props}
                  value={assignedMemberId}
                  onValueChange={setAssignedMemberId}
                  items={operatorOptions}
                />
              )}
            </Field>
            <Field
              label="Da richiamare entro"
              hint="Una lista senza prossima azione è una lista che nessuno chiamerà."
            >
              {(props) => (
                <Input
                  {...props}
                  type="date"
                  value={nextActionAt}
                  onChange={(event) => setNextActionAt(event.target.value)}
                />
              )}
            </Field>
            <Field label="Chi c’è già" hint="Riconosciuto per email o telefono.">
              {(props) => (
                <Select
                  {...props}
                  value={onDuplicate}
                  onValueChange={(value) => setOnDuplicate(value as typeof onDuplicate)}
                  items={DUPLICATE_OPTIONS}
                />
              )}
            </Field>
          </div>

          <div className="mt-4">
            <Button
              onClick={() => void run(true)}
              disabled={missingRequired.length > 0 || !hasContact || tooLarge}
              loading={importer.isPending}
            >
              Anteprima
            </Button>
          </div>

          {tooLarge ? (
            <Alert variant="danger" className="mt-3">
              Il file ha {mapped.length} righe e il massimo per invio è 500. Dividilo in più file.
            </Alert>
          ) : null}
          {importer.isError ? (
            <Alert variant="danger" className="mt-3">
              {errorMessage(importer.error)}
            </Alert>
          ) : null}
        </section>
      ) : null}

      {/* Passo 3 — l'anteprima, e solo dopo la scrittura */}
      {preview !== null && applied === null ? (
        <section className="bg-card rounded-lg border p-4">
          <h2 className="font-semibold">3. Controlla, poi importa</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Niente è stato scritto. <strong>Guarda chi è già iscritto</strong>: quelle righe non
            diventeranno contatti, ed è la ragione per cui questa anteprima esiste.
          </p>
          <ResultSummary result={preview} />
          <RowTable result={preview} />

          <div className="mt-4 flex items-center gap-3">
            <Button onClick={() => void run(false)} loading={importer.isPending}>
              <Upload className="size-4" />
              Importa {preview.created + preview.updated} righe
            </Button>
          </div>
        </section>
      ) : null}

      {applied !== null ? (
        <section className="bg-card rounded-lg border p-4">
          <h2 className="font-semibold">Importazione eseguita</h2>
          <ResultSummary result={applied} />
          <RowTable result={applied} />
          <Button variant="ghost" asChild className="mt-4">
            <Link to="/contatti">Vai ai contatti</Link>
          </Button>
        </section>
      ) : null}
    </div>
  )
}

function ResultSummary({ result }: { result: LeadImportResult }) {
  // «Già iscritti» ha una marca sua e non sta in «già presenti»: sono due cose diverse, e
  // quella che conta è la prima — un cliente che rientra nel funnel riceve l'invito a provare
  // gratis la palestra di cui paga l'abbonamento.
  const alreadyMember = result.rows.filter((row) => row.code === 'alreadyMember').length
  const duplicates = result.skipped - alreadyMember
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      <Badge variant="success">{result.created} da creare</Badge>
      <Badge variant="secondary">{result.updated} da aggiornare</Badge>
      <Badge variant="secondary">{duplicates} già in elenco</Badge>
      {alreadyMember > 0 ? (
        <Badge variant="warning">{alreadyMember} già iscritti, tenuti fuori</Badge>
      ) : null}
      {result.failed > 0 ? <Badge variant="danger">{result.failed} da correggere</Badge> : null}
    </div>
  )
}

/**
 * Le righe con un esito che richiede attenzione, **prima** i già iscritti.
 *
 * Non tutte: una lista di cinquecento contatti nuovi non ha niente da rivedere, e mostrarli
 * seppellirebbe le dodici che contano. L'ordine non è casuale — chi è già cliente sta in cima,
 * perché è la sola informazione che questa anteprima porta e che nessun altro posto darebbe.
 */
function RowTable({ result }: { result: LeadImportResult }) {
  const rank = (code: string | null, outcome: string) =>
    code === 'alreadyMember' ? 0 : outcome === 'failed' ? 1 : 2
  const interesting = result.rows
    .filter((row) => row.outcome !== 'created')
    .sort((a, b) => rank(a.code, a.outcome) - rank(b.code, b.outcome))

  if (interesting.length === 0) {
    return (
      <p className="text-muted-foreground mt-3 text-sm">
        Tutte le righe sono persone nuove: niente da rivedere.
      </p>
    )
  }

  return (
    <ul className="mt-3 divide-y rounded-md border text-sm">
      {interesting.slice(0, 100).map((row) => (
        <li key={row.index} className="flex items-start gap-2 p-2">
          {row.code === 'alreadyMember' ? (
            <UserCheck className="text-warning-content mt-0.5 size-4 shrink-0" />
          ) : row.outcome === 'failed' ? (
            <XCircle className="text-danger-content mt-0.5 size-4 shrink-0" />
          ) : (
            <CheckCircle2 className="text-muted-foreground mt-0.5 size-4 shrink-0" />
          )}
          <div className="min-w-0">
            <p>
              Riga {row.line ?? row.index + 1} ·{' '}
              {row.code === 'alreadyMember'
                ? 'già iscritta, non diventa un contatto'
                : row.outcome === 'failed'
                  ? 'da correggere'
                  : row.outcome === 'skipped'
                    ? 'già in elenco, lasciata stare'
                    : 'già in elenco, da aggiornare'}
              {row.matchedBy !== null ? (
                <span className="text-muted-foreground">
                  {' '}
                  (riconosciuta per {row.matchedBy === 'email' ? 'email' : 'telefono'})
                </span>
              ) : null}
            </p>
            {row.message !== null ? (
              <p className="text-muted-foreground text-xs">{row.message}</p>
            ) : null}
            {row.gymMemberId !== null ? (
              <Link
                to="/iscritti/$gymMemberId"
                params={{ gymMemberId: row.gymMemberId }}
                className="text-primary-content text-xs underline"
              >
                Apri la scheda dell’iscritto
              </Link>
            ) : null}
          </div>
        </li>
      ))}
      {interesting.length > 100 ? (
        <li className="text-muted-foreground p-2 text-xs">
          …e altre {interesting.length - 100} righe.
        </li>
      ) : null}
    </ul>
  )
}
